/**
 * @file OverviewTiles.tsx
 * @description Три плитки «Обзора» платформы (phase19.29): «Пользователи»,
 * «Интеграции», «Последняя копия».
 *
 * Каждая плитка получает свой источник (`OverviewSource`) и превращает
 * готовые данные в значение и пояснение; рамку, ссылку и состояния
 * загрузки/ошибки рисует `OverviewTile`. Пояснения — только из реальных
 * признаков: нечего сказать — строки нет, а не «—».
 */

import { DatabaseBackup, Settings2, ShieldUser } from "lucide-react";

import { Badge } from "../../ui/badge";
import { BACKUP_RUN_BADGE } from "../status-meta";
import { OverviewTile, TileValue } from "./OverviewTile";
import {
  summarizeIntegrations,
  summarizeUsers,
  type BackupOverview,
} from "./overview-model";
import type { OverviewSource } from "./use-overview-source";
import type { AdminUser } from "../../../lib/api-admin-users";
import { RUN_STATUS_LABEL, formatDuration } from "../../../lib/api-backups";
import type { PlatformIntegrationDto } from "../../../lib/api-platform-integrations";
import { pluralRu } from "../../../lib/plural";

interface TileProps<T> {
  source: OverviewSource<T>;
  onRetry: () => void;
}

/** Общие пропсы состояния для `OverviewTile`. */
function tileState<T>(source: OverviewSource<T>) {
  if (source.status === "error") return { status: "error" as const, errorMessage: source.message };
  if (source.status === "ready") return { status: "ready" as const };
  return { status: "loading" as const };
}

export function UsersTile({ source, onRetry }: TileProps<AdminUser[]>) {
  const summary = source.status === "ready" ? summarizeUsers(source.data) : null;
  const hints = summary
    ? [
        summary.neverLoggedIn > 0 ? `${summary.neverLoggedIn} ещё не входили` : null,
        summary.disabled > 0 ? `${summary.disabled} отключено` : null,
      ].filter(Boolean)
    : [];
  return (
    <OverviewTile to="/platform/users" icon={ShieldUser} label="Пользователи" onRetry={onRetry} {...tileState(source)}>
      {summary ? (
        <TileValue
          value={summary.active}
          hint={hints.length > 0 ? hints.join(" · ") : "Все уже входили в кабинет"}
        />
      ) : null}
    </OverviewTile>
  );
}

export function IntegrationsTile({ source, onRetry }: TileProps<PlatformIntegrationDto[]>) {
  const summary = source.status === "ready" ? summarizeIntegrations(source.data) : null;
  const hints = summary
    ? [
        summary.errors > 0 ? `${summary.errors} с ошибкой` : null,
        summary.paused > 0 ? `${summary.paused} на паузе` : null,
      ].filter(Boolean)
    : [];
  return (
    <OverviewTile to="/platform/integrations" icon={Settings2} label="Интеграции" onRetry={onRetry} {...tileState(source)}>
      {summary ? (
        <TileValue
          value={summary.active}
          hint={
            summary.total === 0
              ? "Ни одна интеграция не подключена"
              : [`${pluralRu(summary.active, ["работает", "работают", "работают"])} из ${summary.total}`, ...hints].join(" · ")
          }
        />
      ) : null}
    </OverviewTile>
  );
}

/** Время запуска коротко: «02.10, 01:09». */
function shortDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function BackupTile({ source, onRetry }: TileProps<BackupOverview>) {
  const data = source.status === "ready" ? source.data : null;
  const run = data?.lastRun ?? null;
  return (
    <OverviewTile to="/platform/backups" icon={DatabaseBackup} label="Последняя копия" onRetry={onRetry} {...tileState(source)}>
      {data && run ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="type-metric text-on-surface">
              {shortDateTime(run.finishedAt ?? run.startedAt)}
            </span>
            <Badge variant={BACKUP_RUN_BADGE[run.status]}>{RUN_STATUS_LABEL[run.status]}</Badge>
          </div>
          <div className="text-sm text-on-surface-variant">
            {run.durationMs !== null ? `Длительность ${formatDuration(run.durationMs)}` : null}
            {run.durationMs !== null && !data.config.isEnabled ? " · " : null}
            {!data.config.isEnabled ? "Копирование по расписанию выключено" : null}
          </div>
        </div>
      ) : null}
      {data && !run ? (
        <TileValue
          kind="text"
          value="Копий ещё не было"
          hint={data.config.isEnabled ? "Первая копия появится по расписанию" : "Копирование по расписанию выключено"}
        />
      ) : null}
    </OverviewTile>
  );
}
